import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EntityAutocomplete } from "@/components/EntityAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DocumentPreview } from "@/components/DocumentPreview";
import { FileText, Upload, FileSearch, Eye, RefreshCw, Download, ChevronLeft, ChevronRight } from "lucide-react";

const TIPO_LABELS = { carta_instrucciones: "Carta de instrucciones", factura_carrier: "Factura de carrier" };
const ESTADO_META = {
  procesando: { label: "Procesando", cls: "bg-slate-100 text-slate-700" },
  extraido: { label: "Extraido", cls: "bg-blue-100 text-blue-700" },
  confirmado: { label: "Confirmado", cls: "bg-green-100 text-green-700" },
  rechazado: { label: "Rechazado", cls: "bg-red-100 text-red-700" },
  error: { label: "Error", cls: "bg-orange-100 text-orange-700" },
};
const VEREDICTO_CLS = {
  valida_para_pago: "bg-green-100 text-green-700",
  ya_pagada: "bg-red-100 text-red-700",
  posible_duplicado: "bg-red-100 text-red-700",
  monto_no_coincide: "bg-amber-100 text-amber-700",
  load_invalidado: "bg-red-100 text-red-700",
  no_registrada: "bg-red-100 text-red-700",
  requiere_seleccion: "bg-blue-100 text-blue-700",
};
const VEREDICTO_OPTIONS = Object.keys(VEREDICTO_CLS);
const LIMIT = 20;

export default function Documents() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canCrear = can("documents", "crear");
  const canEditar = can("documents", "editar");
  const [data, setData] = useState({ items: [], total: 0 });
  const [tipo, setTipo] = useState("");
  const [estado, setEstado] = useState("");
  const [veredicto, setVeredicto] = useState("");
  const [carrierFilter, setCarrierFilter] = useState({ id: "", label: "" });
  const [clientFilter, setClientFilter] = useState({ id: "", label: "" });
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [revalidatingId, setRevalidatingId] = useState(null);
  const [reprocessingId, setReprocessingId] = useState(null);

  const buildParams = useCallback(() => {
    const params = {};
    if (tipo) params.tipo_documento = tipo;
    if (estado) params.estado_procesamiento = estado;
    if (veredicto) params.veredicto = veredicto;
    if (carrierFilter.id) params.carrier_id = carrierFilter.id;
    if (clientFilter.id) params.client_id = clientFilter.id;
    if (fechaDesde) params.fecha_desde = fechaDesde;
    if (fechaHasta) params.fecha_hasta = fechaHasta;
    if (search) params.search = search;
    return params;
  }, [tipo, estado, veredicto, carrierFilter.id, clientFilter.id, fechaDesde, fechaHasta, search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...buildParams(), skip: page * LIMIT, limit: LIMIT };
      const { data: res } = await api.get("/documents", { params });
      setData(res);
    } finally {
      setLoading(false);
    }
  }, [buildParams, page]);

  useEffect(() => { setPage(0); }, [tipo, estado, veredicto, carrierFilter.id, clientFilter.id, fechaDesde, fechaHasta, search]);
  useEffect(() => { load(); }, [load]);

  const isStale = (recalculadoEn) => {
    if (!recalculadoEn) return false;
    return Date.now() - new Date(recalculadoEn).getTime() > 24 * 60 * 60 * 1000;
  };

  const revalidar = async (docId) => {
    setRevalidatingId(docId);
    try {
      await api.post(`/documents/${docId}/revalidar`);
      toast.success("Veredicto revalidado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRevalidatingId(null);
    }
  };

  const reprocesar = async (docId) => {
    setReprocessingId(docId);
    try {
      await api.post(`/documents/${docId}/reprocesar`);
      toast.success("Documento reprocesado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setReprocessingId(null);
    }
  };

  const exportFile = async (fmt) => {
    try {
      const res = await api.get(`/documents/export/${fmt}`, { params: buildParams(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `documentos.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error("No se pudo exportar");
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / LIMIT));

  return (
    <div>
      <PageHeader
        title="Documentos"
        subtitle="Biblioteca de cartas de instrucciones y facturas procesadas con IA"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => exportFile("csv")} className="gap-1" data-testid="documents-export-csv">
              <Download className="h-4 w-4" /> CSV
            </Button>
            <Button variant="outline" onClick={() => exportFile("xlsx")} className="gap-1" data-testid="documents-export-xlsx">
              <Download className="h-4 w-4" /> Excel
            </Button>
            {canCrear && (
              <>
                <Button variant="outline" onClick={() => navigate("/documents/nueva-factura")} className="gap-2" data-testid="documents-goto-invoice">
                  <FileSearch className="h-4 w-4" /> Validar factura
                </Button>
                <Button onClick={() => navigate("/documents/nuevo-load")} className="gap-2" data-testid="documents-goto-load">
                  <Upload className="h-4 w-4" /> Nuevo load desde documento
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Input placeholder="Buscar por nombre de archivo..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" data-testid="documents-search-input" />
        <Select value={tipo || "todos"} onValueChange={(v) => setTipo(v === "todos" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="documents-filter-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            <SelectItem value="carta_instrucciones">Carta de instrucciones</SelectItem>
            <SelectItem value="factura_carrier">Factura de carrier</SelectItem>
          </SelectContent>
        </Select>
        <Select value={estado || "todos"} onValueChange={(v) => setEstado(v === "todos" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="documents-filter-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            <SelectItem value="procesando">Procesando</SelectItem>
            <SelectItem value="extraido">Extraido</SelectItem>
            <SelectItem value="confirmado">Confirmado</SelectItem>
            <SelectItem value="rechazado">Rechazado</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
        <Select value={veredicto || "todos"} onValueChange={(v) => setVeredicto(v === "todos" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="documents-filter-veredicto"><SelectValue placeholder="Veredicto" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los veredictos</SelectItem>
            {VEREDICTO_OPTIONS.map((v) => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="w-56">
          <EntityAutocomplete type="carriers" value={carrierFilter.id} label={carrierFilter.label}
            placeholder="Filtrar por carrier..."
            onSelect={(c) => setCarrierFilter({ id: c.id, label: `${c.codigo} - ${c.razon_social}` })} testId="documents-filter-carrier" />
        </div>
        <div className="w-56">
          <EntityAutocomplete type="clients" value={clientFilter.id} label={clientFilter.label}
            placeholder="Filtrar por cliente..."
            onSelect={(c) => setClientFilter({ id: c.id, label: `${c.codigo} - ${c.razon_social}` })} testId="documents-filter-client" />
        </div>
        <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="w-40" data-testid="documents-filter-fecha-desde" />
        <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="w-40" data-testid="documents-filter-fecha-hasta" />
        {(carrierFilter.id || clientFilter.id || fechaDesde || fechaHasta || veredicto) && (
          <Button variant="ghost" size="sm" onClick={() => { setCarrierFilter({ id: "", label: "" }); setClientFilter({ id: "", label: "" }); setFechaDesde(""); setFechaHasta(""); setVeredicto(""); }} data-testid="documents-clear-filters">
            Limpiar filtros
          </Button>
        )}
      </div>

      <div className="rounded-md border border-border bg-white" data-testid="documents-table-container">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Archivo</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Veredicto</TableHead>
              <TableHead>Carrier / Cliente</TableHead>
              <TableHead>Load</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground" data-testid="documents-empty-state">
                Aun no hay documentos. Sube una carta de instrucciones o una factura para comenzar.
              </TableCell></TableRow>
            )}
            {data.items.map((doc, i) => (
              <TableRow key={doc.id} data-testid={`documents-row-${i}`}>
                <TableCell className="flex items-center gap-2 font-medium"><FileText className="h-4 w-4 text-muted-foreground" /> {doc.archivo_nombre}</TableCell>
                <TableCell className="text-sm">{TIPO_LABELS[doc.tipo_documento]}</TableCell>
                <TableCell className="text-sm">{doc.created_at?.slice(0, 10)}</TableCell>
                <TableCell><Badge className={ESTADO_META[doc.estado_procesamiento]?.cls}>{ESTADO_META[doc.estado_procesamiento]?.label || doc.estado_procesamiento}</Badge></TableCell>
                <TableCell>
                  {doc.veredicto && (
                    <div>
                      <Badge className={VEREDICTO_CLS[doc.veredicto]}>{doc.veredicto.replace(/_/g, " ")}</Badge>
                      {doc.veredicto_recalculado_en && (
                        <p className="mt-0.5 text-[10px] text-muted-foreground">
                          Calculado el {new Date(doc.veredicto_recalculado_en).toLocaleString("es-MX")}
                        </p>
                      )}
                      {isStale(doc.veredicto_recalculado_en) && (
                        <div className="mt-1 flex items-center gap-1">
                          <Badge className="bg-orange-100 text-orange-700" data-testid={`documents-row-${i}-stale-badge`}>Puede estar desactualizado</Badge>
                          {canEditar && (
                            <Button size="sm" variant="outline" className="h-6 gap-1 px-2 text-[10px]" onClick={() => revalidar(doc.id)} disabled={revalidatingId === doc.id} data-testid={`documents-row-${i}-revalidar-btn`}>
                              <RefreshCw className={`h-3 w-3 ${revalidatingId === doc.id ? "animate-spin" : ""}`} /> Revalidar
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {doc.estado_procesamiento === "error" && canCrear && (
                    <Button size="sm" variant="outline" className="mt-1 h-6 gap-1 px-2 text-[10px]" onClick={() => reprocesar(doc.id)} disabled={reprocessingId === doc.id} data-testid={`documents-row-${i}-reprocesar-btn`}>
                      <RefreshCw className={`h-3 w-3 ${reprocessingId === doc.id ? "animate-spin" : ""}`} /> Reprocesar
                    </Button>
                  )}
                </TableCell>
                <TableCell className="text-sm">{doc.carrier_nombre || doc.client_nombre || "-"}</TableCell>
                <TableCell className="text-sm font-mono">{doc.numero_load || "-"}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => setPreviewDoc(doc)} data-testid={`documents-preview-btn-${i}`}><Eye className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground" data-testid="documents-pagination">
        <span data-testid="documents-total-count">{data.total} documento(s) en total</span>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="documents-page-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span data-testid="documents-page-label">Pagina {page + 1} de {totalPages}</span>
          <Button size="icon" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="documents-page-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl">
          <DialogHeader><DialogTitle className="font-heading">{previewDoc?.archivo_nombre}</DialogTitle></DialogHeader>
          {previewDoc && <div className="h-[65vh]"><DocumentPreview docId={previewDoc.id} mimeType={previewDoc.archivo_tipo} testId="documents-preview-modal" /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
